import React, { useState, useEffect } from 'react';
import { getEmployees, getEmployeeById, getEmployeeFuelRecords, getDailySummary, resetEmployeePassword, getAllFuelRates, updateFuelRate, getFuelRateHistory, getFuelRateStatistics, getAttendance } from '../../services/api';
import AddEmployee from './AddEmployee';

const AdminDashboard = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [dailySummary, setDailySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    employeeId: null,
    newPassword: '',
    confirmNewPassword: ''
  });
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  
  // Date filter state
  const [filterDate, setFilterDate] = useState('');
  
  // Fuel rates state
  const [fuelRates, setFuelRates] = useState({
    Petrol: 0,
    Diesel: 0,
    Power: 0,
    Oil: 0,
    Additive: 0
  });
  const [editingRate, setEditingRate] = useState(null);
  const [newRateValue, setNewRateValue] = useState('');
  
  // Fuel rate history state
  const [fuelRateHistory, setFuelRateHistory] = useState({});
  const [showHistory, setShowHistory] = useState({});
  
  // Fuel rate statistics state
  const [fuelRateStatistics, setFuelRateStatistics] = useState({});
  const [showStatistics, setShowStatistics] = useState(false);
  
  // Attendance reporting state
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [allAttendance, setAllAttendance] = useState({});
  const [selectedMonth, setSelectedMonth] = useState('');
  const [startDateValue, setStartDateValue] = useState('');
  const [endDateValue, setEndDateValue] = useState('');
  // Add a state for last refresh time
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [employeesResponse, summaryResponse, ratesResponse] = await Promise.all([
          getEmployees(),
          getDailySummary(),
          getAllFuelRates()
        ]);
        
        setEmployees(employeesResponse.data);
        setDailySummary(summaryResponse.data);
        setFuelRates(ratesResponse.data);
        
        // Fetch attendance data for all employees
        const attendanceData = {};
        for (const employee of employeesResponse.data) {
          const response = await getAttendance(employee.id);
          attendanceData[employee.id] = response.data;
        }
        setAllAttendance(attendanceData);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Refresh data when switching tabs
  useEffect(() => {
    if (activeTab === 'employees') {
      refreshEmployeesData();
    } else if (activeTab === 'overview') {
      refreshData();
    }
  }, [activeTab]);

  // Auto-refresh data every 30 seconds when on overview tab
  useEffect(() => {
    if (activeTab !== 'overview') return;
    
    const interval = setInterval(() => {
      refreshData();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [activeTab]);

  // Function to refresh all data
  const refreshData = async () => {
    setLoading(true);
    try {
      const [employeesResponse, summaryResponse, ratesResponse] = await Promise.all([
        getEmployees(),
        getDailySummary(),
        getAllFuelRates()
      ]);
      
      setEmployees(employeesResponse.data);
      setDailySummary(summaryResponse.data);
      setFuelRates(ratesResponse.data);
      
      // Fetch attendance data for all employees
      const attendanceData = {};
      for (const employee of employeesResponse.data) {
        const response = await getAttendance(employee.id);
        attendanceData[employee.id] = response.data;
      }
      setAllAttendance(attendanceData);
      
      // Update refresh timestamp
      setLastRefresh(Date.now());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh only employees data
  const refreshEmployeesData = async () => {
    setLoading(true);
    try {
      const employeesResponse = await getEmployees();
      setEmployees(employeesResponse.data);
      
      // Fetch attendance data for all employees
      const attendanceData = {};
      for (const employee of employeesResponse.data) {
        const response = await getAttendance(employee.id);
        attendanceData[employee.id] = response.data;
      }
      setAllAttendance(attendanceData);
    } catch (error) {
      console.error('Error fetching employees data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = async (employeeId) => {
    setLoading(true);
    
    try {
      const [employeeResponse, recordsResponse] = await Promise.all([
        getEmployeeById(employeeId),
        getEmployeeFuelRecords(employeeId, filterDate || undefined)
      ]);
      
      
      setSelectedEmployee(employeeResponse.data);
      setEmployeeRecords(recordsResponse.data);
      // Note: We're not fetching attendance here anymore as it's handled separately with date filtering
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilterChange = (e) => {
    setFilterDate(e.target.value);
  };

  const applyDateFilter = async () => {
    if (selectedEmployee) {
      setLoading(true);
      try {
        const recordsResponse = await getEmployeeFuelRecords(selectedEmployee.id, filterDate || undefined);
        setEmployeeRecords(recordsResponse.data);
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const clearDateFilter = async () => {
    setFilterDate('');
    if (selectedEmployee) {
      setLoading(true);
      try {
        const recordsResponse = await getEmployeeFuelRecords(selectedEmployee.id);
        setEmployeeRecords(recordsResponse.data);
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEmployeeAdded = (newEmployee) => {
    setEmployees([...employees, newEmployee]);
    setShowAddEmployee(false);
  };

  const handleResetPasswordClick = (employeeId) => {
    setResetPasswordData({
      employeeId,
      newPassword: '',
      confirmNewPassword: ''
    });
    setResetPasswordError('');
    setResetPasswordSuccess(false);
    setShowResetPassword(true);
  };

  const handleResetPasswordChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetPasswordLoading(true);
    setResetPasswordError('');
    setResetPasswordSuccess(false);

    // Validate passwords
    if (resetPasswordData.newPassword.length < 6) {
      setResetPasswordError('Password must be at least 6 characters long');
      setResetPasswordLoading(false);
      return;
    }

    if (resetPasswordData.newPassword !== resetPasswordData.confirmNewPassword) {
      setResetPasswordError('Passwords do not match');
      setResetPasswordLoading(false);
      return;
    }

    try {
      await resetEmployeePassword(resetPasswordData.employeeId, resetPasswordData.newPassword);
      setResetPasswordSuccess(true);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowResetPassword(false);
        setResetPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      setResetPasswordError(error.response?.data?.message || 'Error resetting password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Fuel rates functions
  const handleEditRate = (fuelType, currentRate) => {
    setEditingRate(fuelType);
    setNewRateValue(currentRate.toString());
  };

  const handleSaveRate = async (fuelType) => {
    const rate = parseFloat(newRateValue);
    if (isNaN(rate) || rate <= 0) {
      alert('Please enter a valid rate');
      return;
    }

    try {
      console.log('Updating fuel rate for:', fuelType, 'with rate:', rate);
      const response = await updateFuelRate(fuelType, rate);
      console.log('Fuel rate update response:', response);
      setFuelRates(prev => ({
        ...prev,
        [fuelType]: rate
      }));
      setEditingRate(null);
      setNewRateValue('');
      
      // Refresh history for this fuel type
      fetchFuelRateHistory(fuelType);
    } catch (error) {
      console.error('Error updating fuel rate:', error);
      console.error('Error response:', error.response);
      alert('Error updating fuel rate: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingRate(null);
    setNewRateValue('');
  };

  // Fuel rate history functions
  const fetchFuelRateHistory = async (fuelType) => {
    try {
      const response = await getFuelRateHistory(fuelType);
      setFuelRateHistory(prev => ({
        ...prev,
        [fuelType]: response.data
      }));
    } catch (error) {
      console.error(`Error fetching history for ${fuelType}:`, error);
    }
  };

  const toggleHistory = async (fuelType) => {
    const newShowHistory = {
      ...showHistory,
      [fuelType]: !showHistory[fuelType]
    };
    
    setShowHistory(newShowHistory);
    
    // Fetch history if we're showing it and don't have it yet
    if (newShowHistory[fuelType] && !fuelRateHistory[fuelType]) {
      await fetchFuelRateHistory(fuelType);
    }
  };

  // Fuel rate statistics functions
  const fetchFuelRateStatistics = async () => {
    try {
      const response = await getFuelRateStatistics();
      // Convert array to object with fuel_type as keys
      const stats = {};
      response.data.forEach(stat => {
        stats[stat.fuel_type] = stat;
      });
      setFuelRateStatistics(stats);
    } catch (error) {
      console.error('Error fetching fuel rate statistics:', error);
    }
  };

  const toggleStatistics = async () => {
    const newShowStatistics = !showStatistics;
    setShowStatistics(newShowStatistics);
    
    // Fetch statistics if we're showing them and don't have them yet
    if (newShowStatistics && Object.keys(fuelRateStatistics).length === 0) {
      await fetchFuelRateStatistics();
    }
  };

  // Calculate overall totals
  const totalSales = dailySummary.reduce((sum, day) => sum + (parseInt(day.total_sales) || 0), 0);
  const totalQuantity = dailySummary.reduce((sum, day) => sum + (parseFloat(day.total_quantity) || 0), 0);
  const totalRevenue = dailySummary.reduce((sum, day) => sum + (parseFloat(day.total_revenue) || 0), 0);

  const fetchEmployeeAttendance = async (employeeId, startDate = null, endDate = null) => {
    setLoading(true);
    try {
      const response = await getAttendance(employeeId, startDate, endDate);
      setAttendanceRecords(response.data);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAttendance = async () => {
    try {
      const attendanceData = {};
      const allRecords = [];
      
      for (const employee of employees) {
        const response = await getAttendance(employee.id);
        attendanceData[employee.id] = response.data;
        // Add employee_id to each record for identification
        response.data.forEach(record => {
          allRecords.push({ ...record, employee_id: employee.id });
        });
      }
      
      setAllAttendance(attendanceData);
      // When showing all employees, set attendanceRecords to all records
      if (!selectedEmployee) {
        setAttendanceRecords(allRecords);
      }
    } catch (error) {
      console.error('Error fetching all attendance records:', error);
    }
  };

  const handleFilterAttendance = async () => {
    setLoading(true);
    try {
      let startDate = null;
      let endDate = null;
      
      if (startDateValue) {
        startDate = startDateValue;
      }
      
      if (endDateValue) {
        endDate = endDateValue;
      }
      
      // If only startDate is provided, set endDate to the end of that month
      if (startDate && !endDate) {
        const start = new Date(startDate);
        const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        endDate = lastDay.toISOString().split('T')[0];
      }
      
      // If only endDate is provided, set startDate to the beginning of that month
      if (!startDate && endDate) {
        const end = new Date(endDate);
        const firstDay = new Date(end.getFullYear(), end.getMonth(), 1);
        startDate = firstDay.toISOString().split('T')[0];
      }
      
      // If an employee is selected, filter for that employee
      // Otherwise, get attendance for all employees
      if (selectedEmployee) {
        const response = await getAttendance(selectedEmployee.id, startDate, endDate);
        setAttendanceRecords(response.data);
      } else {
        // For all employees, fetch data for each employee with date filters
        const attendanceData = {};
        const allRecords = [];
        
        for (const employee of employees) {
          const response = await getAttendance(employee.id, startDate, endDate);
          attendanceData[employee.id] = response.data;
          // Add employee_id to each record for identification
          response.data.forEach(record => {
            allRecords.push({ ...record, employee_id: employee.id });
          });
        }
        
        setAllAttendance(attendanceData);
        setAttendanceRecords(allRecords);
      }
    } catch (error) {
      console.error('Error filtering attendance records:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearAttendanceFilter = async () => {
    setStartDateValue('');
    setEndDateValue('');
    if (selectedEmployee) {
      await fetchEmployeeAttendance(selectedEmployee.id);
    } else {
      // Refresh all attendance data when no employee is selected
      await fetchAllAttendance();
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-2 px-2 sm:px-4 lg:px-6">
      <div className="py-2 sm:px-0">
        <div className="mb-3">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">Admin Dashboard</h1>
        </div>
        
        {/* Tabs for mobile */}
        <div className="md:hidden mb-4">
          <label htmlFor="tabs" className="sr-only">Select a tab</label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}>
            <option value="overview">Overview</option>
            <option value="employees">Employees</option>
            <option value="fuelRates">Fuel Rates</option>
            <option value="attendance">Attendance</option>
            <option value="reports">Reports</option>
          </select>
        </div>
        
        {/* Tabs for larger screens - only show when NOT on mobile */}
        <div className="hidden md:block mb-4">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === 'overview' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                Overview
              </button>
              <button
                onClick={() => setActiveTab('employees')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === 'employees' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                Employees
              </button>
              <button
                onClick={() => setActiveTab('fuelRates')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === 'fuelRates' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                Fuel Rates
              </button>
              <button
                onClick={() => setActiveTab('attendance')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === 'attendance' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                Attendance
              </button>
              {/* <button
                onClick={() => setActiveTab('reports')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs sm:text-sm ${activeTab === 'reports' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                Reports
              </button> */}
            </nav>
          </div>
        </div>
        
        {activeTab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-4 mb-4">
              <div className="bg-white overflow-hidden shadow rounded-md">
                <div className="px-3 py-4 sm:p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-primary-500 rounded-md p-2 sm:p-1.5">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 w-0 flex-1">
                      <dl>
                        <dt className="text-xs font-medium text-gray-500 truncate">Total Employees</dt>
                        <dd className="flex items-baseline">
                          <div className="text-base sm:text-lg font-semibold text-gray-900">{employees.length}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-md">
                <div className="px-3 py-4 sm:p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-green-500 rounded-md p-2 sm:p-1.5">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="ml-3 w-0 flex-1">
                      <dl>
                        <dt className="text-xs font-medium text-gray-500 truncate">Total Sales</dt>
                        <dd className="flex items-baseline">
                          <div className="text-base sm:text-lg font-semibold text-gray-900">{totalSales}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-md">
                <div className="px-3 py-4 sm:p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-blue-500 rounded-md p-2 sm:p-1.5">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 w-0 flex-1">
                      <dl>
                        <dt className="text-xs font-medium text-gray-500 truncate">Total Revenue</dt>
                        <dd className="flex items-baseline">
                          <div className="text-base sm:text-lg font-semibold text-gray-900">
                            ₹{(typeof totalRevenue === 'number' ? totalRevenue : parseFloat(totalRevenue) || 0).toFixed(2)}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-md">
                <div className="px-3 py-4 sm:p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-purple-500 rounded-md p-2 sm:p-1.5">
                      <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 7v5l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 w-0 flex-1">
                      <dl>
                        <dt className="text-xs font-medium text-gray-500 truncate">Fuel Sold</dt>
                        <dd className="flex items-baseline">
                          <div className="text-base sm:text-lg font-semibold text-gray-900">
                            {(typeof totalQuantity === 'number' ? totalQuantity : parseFloat(totalQuantity) || 0).toFixed(2)} L
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Daily Summary with Date Filter */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-3 py-3 sm:px-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-sm sm:text-base leading-5 font-medium text-gray-900">Daily Summary</h3>
                  <div className="mt-2 sm:mt-0 text-xs text-gray-500">
                    Showing last 30 days
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Sales
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity (L)
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : dailySummary.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 text-center">
                          No data available
                        </td>
                      </tr>
                    ) : (
                      dailySummary.map((day) => (
                        <tr key={day.date}>
                          <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                            {day.date}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {day.total_sales}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {(typeof day.total_quantity === 'number' ? day.total_quantity : parseFloat(day.total_quantity) || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {(typeof day.total_revenue === 'number' ? day.total_revenue : parseFloat(day.total_revenue) || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'employees' && (
          <div className={`grid grid-cols-1 gap-4 ${selectedEmployee ? 'lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
            <div className={selectedEmployee ? 'lg:col-span-1' : 'lg:col-span-1'}>
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <div className="px-3 py-3 sm:px-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm sm:text-base leading-5 font-medium text-gray-900">Employees</h3>
                      <p className="mt-1 max-w-2xl text-xs text-gray-500">Select an employee to view details</p>
                    </div>
                    <button
                      onClick={() => setShowAddEmployee(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <svg className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Add Employee
                    </button>
                  </div>
                </div>
                
                {/* Add Employee Form */}
                {showAddEmployee && (
                  <div className="px-3 py-3 sm:px-4 bg-gray-50 border-b border-gray-200">
                    <AddEmployee 
                      onEmployeeAdded={handleEmployeeAdded} 
                      onCancel={() => setShowAddEmployee(false)} 
                    />
                  </div>
                )}
                
                <div className="border-t border-gray-200">
                  <ul className="divide-y divide-gray-200">
                    {loading ? (
                      <li className="px-4 py-3 text-center text-xs text-gray-500">
                        Loading...
                      </li>
                    ) : employees.length === 0 ? (
                      <li className="px-4 py-3 text-center text-xs text-gray-500">
                        No employees found
                      </li>
                    ) : (
                      employees.map((employee) => (
                        <li 
                          key={employee.id} 
                          className={`px-4 py-3 cursor-pointer transition-colors duration-150 ${
                            selectedEmployee && selectedEmployee.id === employee.id 
                              ? 'bg-primary-50 border-l-2 border-primary-500' 
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleEmployeeSelect(employee.id)}
                        >
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-primary-800 font-medium text-sm">
                                  {employee.name.charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-3 min-w-0">
                              <div className="text-xs font-medium text-gray-900 truncate">{employee.name}</div>
                              <div className="text-xs text-gray-500 truncate">{employee.email}</div>
                              <div className="text-xs text-gray-400 capitalize">{employee.role}</div>
                            </div>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </div>
            
            {selectedEmployee && !loading && (
              <div className="lg:col-span-2">
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-3 py-3 sm:px-4">
                    <h3 className="text-sm sm:text-base leading-5 font-medium text-gray-900">
                      {selectedEmployee.name}
                    </h3>
                    <p className="mt-1 max-w-2xl text-xs text-gray-500">
                      Employee details and records
                    </p>
                  </div>
                  <div className="border-t border-gray-200 px-3 py-3 sm:px-4">
                    <dl className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2">
                      <div className="sm:col-span-1">
                        <dt className="text-xs font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-xs text-gray-900">{selectedEmployee.email}</dd>
                      </div>
                      <div className="sm:col-span-1">
                        <dt className="text-xs font-medium text-gray-500">Role</dt>
                        <dd className="mt-1 text-xs text-gray-900 capitalize">{selectedEmployee.role}</dd>
                      </div>
                      <div className="sm:col-span-1">
                        <dt className="text-xs font-medium text-gray-500">Member since</dt>
                        <dd className="mt-1 text-xs text-gray-900">
                          {new Date(selectedEmployee.created_at).toLocaleDateString()}
                        </dd>
                      </div>
                    </dl>
                    
                    {/* Reset Password Button */}
                    <div className="mt-4">
                      <button
                        onClick={() => handleResetPasswordClick(selectedEmployee.id)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg className="-ml-0.5 mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Reset Password
                      </button>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200">
                    <div className="px-3 py-3 sm:px-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <h4 className="text-sm font-medium text-gray-900">Fuel Sales Records</h4>
                        <div className="mt-2 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                          <input
                            type="date"
                            value={filterDate}
                            onChange={handleDateFilterChange}
                            className="block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-xs py-1.5"
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={applyDateFilter}
                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              Apply
                            </button>
                            <button
                              onClick={clearDateFilter}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Fuel Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Quantity (L)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Rate (₹)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total (₹)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : employeeRecords.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                No records found
                              </td>
                            </tr>
                          ) : (
                            employeeRecords.map((record) => (
                              <tr key={record.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {record.fuel_type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {typeof record.quantity === 'number' ? record.quantity : parseFloat(record.quantity) || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {record.rate !== undefined && record.rate !== null ? 
                                    (typeof record.rate === 'number' ? record.rate : parseFloat(record.rate) || 0).toFixed(2) : 
                                    'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {record.total_amount !== undefined && record.total_amount !== null ? 
                                    (typeof record.total_amount === 'number' ? record.total_amount : parseFloat(record.total_amount) || 0).toFixed(2) : 
                                    'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div>{new Date(record.created_at).toLocaleDateString()}</div>
                                  <div className="text-xs text-gray-400">
                                    {new Date(record.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'fuelRates' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Fuel Rates Management</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Set and manage fuel rates for automatic price calculation</p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Fuel Rates Management - Left Column */}
                <div className="lg:col-span-2">
                  <div className="mb-6">
                    <button
                      onClick={toggleStatistics}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 btn-mobile"
                    >
                      {showStatistics ? 'Hide Statistics' : 'Show Statistics'}
                    </button>
                    
                    {showStatistics && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-base font-medium text-gray-900 mb-3">Fuel Rate Change Statistics</h4>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          {Object.entries(fuelRateStatistics).map(([fuelType, stats]) => (
                            <div key={fuelType} className="bg-white p-4 rounded-md shadow">
                              <h5 className="font-medium text-gray-900 text-base">{fuelType}</h5>
                              <div className="mt-2 space-y-1">
                                <p className="text-sm text-gray-600">Total Changes: {stats.total_changes}</p>
                                <p className="text-sm text-gray-600">
                                  Avg Change: {stats.avg_change_percentage ? 
                                    `${(typeof stats.avg_change_percentage === 'number' ? stats.avg_change_percentage : parseFloat(stats.avg_change_percentage) || 0).toFixed(2)}%` : 
                                    'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Max Increase: {stats.max_increase ? 
                                    `${(typeof stats.max_increase === 'number' ? stats.max_increase : parseFloat(stats.max_increase) || 0).toFixed(2)}%` : 
                                    'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Max Decrease: {stats.max_decrease ? 
                                    `${(typeof stats.max_decrease === 'number' ? stats.max_decrease : parseFloat(stats.max_decrease) || 0).toFixed(2)}%` : 
                                    'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Last Updated: {new Date(stats.last_updated).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="overflow-hidden bg-white shadow sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                      {Object.entries(fuelRates).map(([fuelType, rate]) => (
                        <li key={fuelType} className="px-4 py-5">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-base font-medium text-gray-900 mb-3 sm:mb-0">{fuelType}</div>
                            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                              {editingRate === fuelType ? (
                                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                                  <input
                                    type="number"
                                    value={newRateValue}
                                    onChange={(e) => setNewRateValue(e.target.value)}
                                    className="block w-full sm:w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 form-input-mobile"
                                    step="0.01"
                                    min="0"
                                  />
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleSaveRate(fuelType)}
                                      className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 btn-mobile"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 btn-mobile"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                                  <div className="text-base font-semibold text-gray-900">
                                    ₹{(typeof rate === 'number' ? rate : parseFloat(rate) || 0).toFixed(2)}/L
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleEditRate(fuelType, rate)}
                                      className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 btn-mobile"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => toggleHistory(fuelType)}
                                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 btn-mobile"
                                    >
                                      {showHistory[fuelType] ? 'Hide History' : 'Show History'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Fuel Rate History */}
                          {showHistory[fuelType] && fuelRateHistory[fuelType] && (
                            <div className="mt-5 ml-0 sm:ml-4 pl-0 sm:pl-4 border-l-0 sm:border-l border-gray-200">
                              <h4 className="text-base font-medium text-gray-700 mb-3">Price History</h4>
                              <div className="max-h-48 overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated By</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {fuelRateHistory[fuelType].map((historyItem) => (
                                      <tr key={historyItem.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                          ₹{(typeof historyItem.new_rate === 'number' ? historyItem.new_rate : parseFloat(historyItem.new_rate) || 0).toFixed(2)}/L
                                        </td>
                                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                                          (typeof historyItem.change_percentage === 'number' ? historyItem.change_percentage : parseFloat(historyItem.change_percentage)) > 0 ? 'text-green-600' : 
                                          (typeof historyItem.change_percentage === 'number' ? historyItem.change_percentage : parseFloat(historyItem.change_percentage)) < 0 ? 'text-red-600' : 'text-gray-500'
                                        }`}>
                                          {historyItem.change_percentage ? 
                                            `${(typeof historyItem.change_percentage === 'number' ? historyItem.change_percentage : parseFloat(historyItem.change_percentage) || 0).toFixed(2)}%` : 
                                            'N/A'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                          {new Date(historyItem.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                          {historyItem.updated_by_name}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Fuel Summary Table - Right Column */}
                <div className="lg:col-span-1">
                  <div className="bg-white shadow rounded-lg p-4">
                    <h4 className="text-base font-medium text-gray-900 mb-4">Fuel Sales Summary</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fuel Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Rate</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Object.entries(fuelRates).map(([fuelType, rate]) => (
                            <tr key={fuelType} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{fuelType}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                ₹{(typeof rate === 'number' ? rate : parseFloat(rate) || 0).toFixed(2)}/L
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="mt-6">
                      <h4 className="text-base font-medium text-gray-900 mb-3">Recent Updates</h4>
                      <div className="space-y-3">
                        {Object.entries(fuelRateHistory)
                          .flatMap(([fuelType, history]) => 
                            history.slice(0, 2).map(item => ({ ...item, fuelType }))
                          )
                          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                          .slice(0, 4)
                          .map((item, index) => (
                            <div key={index} className="flex items-center text-sm">
                              <div className="flex-shrink-0 w-3 h-3 rounded-full bg-primary-500"></div>
                              <div className="ml-2 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{item.fuelType}</p>
                                <p className="text-gray-500 truncate">
                                  ₹{(typeof item.new_rate === 'number' ? item.new_rate : parseFloat(item.new_rate) || 0).toFixed(2)}/L
                                </p>
                              </div>
                              <div className="ml-auto text-gray-500">
                                {new Date(item.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'reports' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Reports</h3>
              <div className="mt-2 max-w-xl text-sm text-gray-500">
                <p>Reports and analytics will be available here.</p>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'attendance' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-3 py-3 sm:px-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm sm:text-base leading-5 font-medium text-gray-900">Attendance Summary</h3>
                  <p className="mt-1 text-xs text-gray-500">View employee attendance records</p>
                </div>
                {/* Month Selector */}
                <div className="mt-3 sm:mt-0 flex items-center gap-2">
                  <label htmlFor="month-select" className="text-sm font-medium text-gray-700">
                    Month:
                  </label>
                  <select
                    id="month-select"
                    className="block rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    value={selectedMonth}
                    onChange={async (e) => {
                      setSelectedMonth(e.target.value);
                      if (e.target.value) {
                        const [year, month] = e.target.value.split('-');
                        const startDate = `${year}-${month}-01`;
                        const lastDay = new Date(year, month, 0).getDate();
                        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
                        
                        // Fetch attendance for all employees with date range
                        setLoading(true);
                        try {
                          const attendanceData = {};
                          for (const employee of employees) {
                            const response = await getAttendance(employee.id, startDate, endDate);
                            attendanceData[employee.id] = response.data;
                          }
                          setAllAttendance(attendanceData);
                        } catch (error) {
                          console.error('Error fetching attendance:', error);
                        } finally {
                          setLoading(false);
                        }
                      } else {
                        // Reset to all time
                        fetchAllAttendance();
                      }
                    }}
                  >
                    <option value="">All Time</option>
                    {(() => {
                      const months = [];
                      const now = new Date();
                      for (let i = 0; i < 12; i++) {
                        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
                        months.push(<option key={`${year}-${month}`} value={`${year}-${month}`}>{monthName}</option>);
                      }
                      return months;
                    })()}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="px-3 py-5 sm:p-6">
              {/* Simple table showing all employees */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Logins
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Logouts
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Activity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {employees.map((employee) => {
                        const employeeAttendance = allAttendance[employee.id] || [];
                        const logins = employeeAttendance.filter(record => record.type === 'login').length;
                        const logouts = employeeAttendance.filter(record => record.type === 'logout').length;
                        const lastActivity = employeeAttendance.length > 0
                          ? employeeAttendance.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
                          : null;
                        
                        return (
                          <tr key={employee.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                              <div className="text-xs text-gray-500">{employee.email}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {logins}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {logouts}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {lastActivity ? (
                                <div>
                                  <div className="flex items-center">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      lastActivity.type === 'login' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {lastActivity.type === 'login' ? 'Login' : 'Logout'}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">
                                    {new Date(lastActivity.timestamp).toLocaleDateString()} {new Date(lastActivity.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">Never</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Reset Password Modal */}
        {showResetPassword && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 sm:w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Reset Password</h3>
                  <button
                    onClick={() => setShowResetPassword(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form onSubmit={handleResetPasswordSubmit} className="mt-4">
                  {resetPasswordError && (
                    <div className="mb-4 rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">
                        {resetPasswordError}
                      </div>
                    </div>
                  )}
                  {resetPasswordSuccess && (
                    <div className="mb-4 rounded-md bg-green-50 p-4">
                      <div className="text-sm text-green-700">
                        Password reset successfully!
                      </div>
                    </div>
                  )}
                  <div className="mb-4">
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={resetPasswordData.newPassword}
                      onChange={handleResetPasswordChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmNewPassword"
                      name="confirmNewPassword"
                      value={resetPasswordData.confirmNewPassword}
                      onChange={handleResetPasswordChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div className="flex justify-end space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(false)}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      disabled={resetPasswordLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                      disabled={resetPasswordLoading}
                    >
                      {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;